/**
 * client/src/lib/auth-me.ts
 *
 * The ONE `/api/auth/me` bootstrap fetch, shared by `useCurrentUser` and
 * `OrgContextProvider` (both subscribe to the same `["auth/me"]` queryKey).
 * Centralized so the timeout + the 401/403 → null mapping cannot DRIFT between
 * the two call sites — a drift that previously stranded the whole app on a
 * permanent `PageLoader` when the bootstrap fetch hung (a server restarted
 * mid-flight leaves an un-timed fetch pending forever, and the bootstrap query
 * used `retry: false`, so it never recovered).
 *
 * The fix is two-part and lives here + in `queryClient.ts`:
 *   1. the fetch carries a timeout (below) so a hung request SETTLES;
 *   2. neither call site sets `retry: false` any more, so both inherit the
 *      resilient global policy (never retries 401/403, retries transient
 *      network/timeout/5xx up to 2×).
 */

import { apiRequest, ApiError } from "@/lib/queryClient";
import type { CurrentUser } from "@/App";

/** The shared queryKey — existing `invalidateQueries(["auth/me"])` callers keep
 *  working (structural key equality). */
export const AUTH_ME_QUERY_KEY = ["auth/me"] as const;

/**
 * Bootstrap fetch timeout. Long enough for a slow-but-alive server, short
 * enough that a dead connection recovers quickly instead of hanging the app.
 */
export const AUTH_ME_TIMEOUT_MS = 10_000;

/**
 * GET `/api/auth/me`.
 *   - resolves to the `CurrentUser` on 2xx;
 *   - resolves to `null` on 401/403 (intentionally logged-out — no retry);
 *   - THROWS on any other failure (5xx, network, timeout) so the caller's
 *     `useQuery` surfaces it via `isError` and retries per the global policy.
 */
export async function fetchAuthMe(): Promise<CurrentUser | null> {
  try {
    return await apiRequest<CurrentUser>("/api/auth/me", undefined, {
      timeoutMs: AUTH_ME_TIMEOUT_MS,
    });
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      return null;
    }
    throw err;
  }
}
